/**
 * MultiSelect Component - Premium Design
 * Enhanced Select with "Select All" functionality
 * Light, airy design with capsule badges for selected values
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Select, Checkbox } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { colors, borderRadius, shadows, spacing } from '../../theme/designTokens';

const { Option } = Select;

/**
 * MultiSelect Component
 *
 * @param {Array} value - Selected values
 * @param {function} onChange - Change handler
 * @param {Array} options - Options array [{value, label}]
 * @param {string} placeholder - Placeholder text (action verb phrase)
 * @param {boolean} showSelectAll - Show select all option (default: true)
 * @param {number} maxTagCount - Max tags to show before "+N" (default: 'responsive')
 * @param {string} size - Select size ('small' | 'default' | 'large')
 * @param {Object} style - Additional styles
 * @param {Object} rest - Additional Select props
 */
function MultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = 'Select items',
  showSelectAll = true,
  maxTagCount = 'responsive',
  size = 'default',
  style = {},
  ...rest
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // All option values
  const allValues = useMemo(() => options.map(opt => opt.value), [options]);

  // Check if all are selected
  const allSelected = useMemo(() => {
    return allValues.length > 0 && allValues.every(v => value.includes(v));
  }, [value, allValues]);

  // Handle select all
  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      onChange(allValues);
    } else {
      onChange([]);
    }
  }, [allValues, onChange]);

  // Custom tag render for capsule badges
  const tagRender = (props) => {
    const { label, closable, onClose } = props;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: colors.form.badgeBackground,
          border: `1px solid ${colors.form.badgeBorder}`,
          borderRadius: borderRadius.badge,
          padding: '2px 6px',
          marginRight: 4,
          fontSize: 12,
          color: colors.form.selectedText,
        }}
      >
        {label}
        {closable && (
          <CloseOutlined
            onClick={onClose}
            style={{
              marginLeft: 4,
              fontSize: 10,
              color: colors.form.iconMuted,
              cursor: 'pointer',
            }}
          />
        )}
      </span>
    );
  };

  // Dropdown render with Select All checkbox
  const dropdownRender = useCallback((menu) => {
    if (!showSelectAll) return menu;

    return (
      <div>
        <div
          style={{
            padding: '8px 12px',
            borderBottom: `1px solid ${colors.form.inputBorder}`,
          }}
        >
          <Checkbox
            checked={allSelected}
            indeterminate={value.length > 0 && !allSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
            style={{
              fontSize: 13,
            }}
          >
            Select All ({allValues.length})
          </Checkbox>
        </div>
        {menu}
      </div>
    );
  }, [showSelectAll, allSelected, value.length, allValues.length, handleSelectAll]);

  return (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxTagCount={maxTagCount}
      maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
      dropdownRender={dropdownRender}
      open={dropdownOpen}
      onDropdownVisibleChange={setDropdownOpen}
      size={size}
      tagRender={tagRender}
      style={{ minWidth: 200, ...style }}
      dropdownStyle={{
        borderRadius: borderRadius.dropdown,
        boxShadow: shadows.dropdown,
      }}
      {...rest}
    >
      {options.map((option) => (
        <Option key={option.value} value={option.value}>
          {option.label || option.value}
        </Option>
      ))}
    </Select>
  );
}

export default MultiSelect;
